import React, { useEffect, useState } from "react";
import { Button, UNSTABLE_Toast } from "react-aria-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  IconDefinition,
  faCircleCheck,
  faExclamationCircle,
  faInfoCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { ToastProps as ReactToastNotificationsToastProps } from "react-toast-notifications";
import { useFocusWithin } from "react-aria";

export function ToastWrapper({ children, ...props }: ReactToastNotificationsToastProps) {
  const { onDismiss, onMouseEnter, onMouseLeave, transitionState, appearance } = props;
  const [isFocused, setIsFocused] = useState(false);

  const pauseAutoDismiss = () => {
    onMouseEnter();
  };

  const resumeAutoDismiss = () => {
    onMouseLeave();
  };

  const changeFocus = (isFocusWithin: boolean) => {
    setIsFocused(isFocusWithin);

    // if unfocused, resume autodismiss
    if (!isFocusWithin) {
      resumeAutoDismiss();
    }
  };

  /**
   * I'm using focusWithin here for the close button and the toast content itself
   */
  const { focusWithinProps } = useFocusWithin({
    onBlurWithin: resumeAutoDismiss,
    onFocusWithin: pauseAutoDismiss,
    onFocusWithinChange: changeFocus,
  });

  /**
   * The useState and useEffects control the slide-in and slide-out animations for the toast
   */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    /**
     * This "transitionState" comes from the react-toast-notification package
     */
    if (transitionState === "exiting") {
      setVisible(false);
    }
  }, [transitionState]);

  return (
    <div className='tw-mb-2' {...focusWithinProps}>
      {/* Dummy toast object, it seems like everything still works fine */}
      <UNSTABLE_Toast
        toast={{ content: null, key: "" }}
        onMouseEnter={pauseAutoDismiss}
        onMouseLeave={() => {
          // Resume auto dismiss, only if the toast is not focused
          if (!isFocused) {
            resumeAutoDismiss();
          }
        }}
      >
        <Toast content={children} onClose={onDismiss} appearance={appearance} visible={visible} />
      </UNSTABLE_Toast>
    </div>
  );
}

interface ToastProps {
  appearance: ToastStatus;
  onClose: () => void;
  content: React.ReactNode;
  visible: boolean;
}

function Toast({ onClose, content, appearance, visible }: ToastProps) {
  const color = ToastTypeColor[appearance];
  const icon = ToastTypeIcon[appearance];
  return (
    <div
      style={{
        borderColor: color,
      }}
      className={`tw-grid tw-max-w-sm tw-grid-cols-9 tw-rounded tw-border-y-0 tw-border-l-4 tw-border-r-0 tw-border-solid tw-bg-white tw-p-3 tw-shadow-[0_2px_12px_0_rgba(55,65,81,0.15)] tw-transition-all tw-duration-500 tw-ease-out ${
        visible ? "tw-translate-x-0 tw-opacity-100" : "tw-translate-x-full tw-opacity-0"
      }`}
    >
      <div className='tw-col-span-1'>
        <FontAwesomeIcon className='tw-mr-2' size='lg' color={color} icon={icon} />{" "}
      </div>
      <div className='tw-col-span-7'>
        <div
          /**
           * The "aria-live='polite'" is important for screen readers @see https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA19
           */
          aria-live='polite'
          className='tw-flex tw-items-center tw-font-[Lato] tw-font-semibold tw-not-italic tw-leading-5 tw-text-[#6B6B6B]'
        >
          {content}
        </div>
      </div>
      <div className='tw-col-span-1 tw-justify-self-end'>
        <Button
          aria-label='close'
          className='tw-border-none tw-bg-transparent tw-p-0 hover:tw-cursor-pointer'
          onClick={onClose}
        >
          <FontAwesomeIcon
            icon={faTimes}
            className='tw-text-[#6B6B6B] hover:tw-text-black focus:tw-text-black'
            size='lg'
          />
        </Button>
      </div>
    </div>
  );
}

/**
 * I have a dream that one day all of the addToast calls will have the value of their
 * 'appearance' property set to one of these enum values instead of a string
 *
 * -- Peter Walsh; Wednesday, August 20, 2025
 */
type ToastStatus = (typeof ToastStatuses)[keyof typeof ToastStatuses];
export const ToastStatuses = {
  Info: "info",
  Success: "success",
  Warning: "warning",
  Error: "error",
} as const;

const ToastTypeColor: Record<ToastStatus, string> = {
  [ToastStatuses.Info]: "#0978FA",
  [ToastStatuses.Success]: "#00AD28",
  [ToastStatuses.Warning]: "#F2711E",
  [ToastStatuses.Error]: "#DF0202",
};

const ToastTypeIcon: Record<ToastStatus, IconDefinition> = {
  [ToastStatuses.Info]: faInfoCircle,
  [ToastStatuses.Success]: faCircleCheck,
  [ToastStatuses.Warning]: faExclamationCircle,
  [ToastStatuses.Error]: faExclamationCircle,
};
